import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useRecentSplit } from '@tunasong/search'
import { Button, EntityList, Typography, useFavorites, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './merged-continue-section.module.css'

export interface MergedContinueSectionProps {
  /** Empty-state CTA — open the Spaces view. */
  onOpenSpaces(): void
  /** Empty-state CTA — start a new song. */
  onNewSong(): void
}

/**
 * Tiny-viewport collapse: fuses `Pinned` + `Continue working` into one section
 * labeled `Continue`. Pinned items render first (in pinOrder), then non-pinned
 * `mine`. Recently updated stays as its own section below to preserve the
 * collaborator-vs-mine distinction.
 */
export const MergedContinueSection: FC<MergedContinueSectionProps> = props => {
  const { onOpenSpaces, onNewSong } = props
  const { mine, isLoading: isLoadingSplit } = useRecentSplit()
  const { pinnedFavorites, isPinned } = useFavorites()
  const navigateToEntity = useNavigateToEntity()

  const pinnedIds = pinnedFavorites
    .map(f => f.entityId)
    .filter((id): id is string => typeof id === 'string')
  const mineIds = mine.map(e => e.id)
  const allIds = [...pinnedIds, ...mineIds.filter(id => !pinnedIds.includes(id))]

  const { existingEntities = [], isLoading: isResolving } = graphHooks.useEntitiesById(allIds)
  const entitiesById = new Map(existingEntities.map(e => [e.id, e]))

  const items: Persisted<Entity>[] = allIds
    .map(id => entitiesById.get(id))
    .filter((e): e is Persisted<Entity> => Boolean(e) && !e?.trash)
    // Pinned items kept first (already at the head of `allIds`); non-pinned items
    // from mine that are also pinned would have been dropped above by `!pinnedIds.includes`.
    .filter(e => pinnedIds.includes(e.id) || !isPinned(e.id))

  const loading = isLoadingSplit || isResolving

  if (!loading && items.length === 0) {
    return (
      <section className={styles.section} aria-labelledby="continue-heading-tiny">
        <Typography id="continue-heading-tiny" className={styles.heading} variant="h2">
          Continue
        </Typography>
        <Typography className={styles.caption} variant="caption">
          Nothing in progress yet — open a Space or create a song to start.
        </Typography>
        <div className={styles.emptyActions}>
          <Button onClick={onOpenSpaces}>Open a Space</Button>
          <Button onClick={onNewSong}>New Song</Button>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.section} aria-labelledby="continue-heading-tiny">
      <Typography id="continue-heading-tiny" className={styles.heading} variant="h2">
        Continue
      </Typography>
      <EntityList
        showBreadcrumbs="inactiveLinks"
        entities={items}
        onOpen={navigateToEntity}
        size="medium"
        actions={false}
      />
    </section>
  )
}

export default MergedContinueSection
